import React, { forwardRef, PropsWithChildren, ReactNode } from "react";
import { Button, Flex, Text } from "@radix-ui/themes";
import styles from "./PageHeader.module.scss";
import { cx } from "class-variance-authority";
import { ChevronLeftIcon } from "@radix-ui/react-icons";

const sizeMap = {
  sm: { sticky: "2", content: "5", py: "2" },
  md: { sticky: "3", content: "7", py: "4" },
  lg: { sticky: "4", content: "8", py: "6" },
} as const;

interface PageHeaderProps {
  icon?: ReactNode;
  title: ReactNode;
  description?: string;
  actions?: ReactNode;
  className?: string;
  sticky?: boolean;
  onBack?: () => void;
  backText?: string;
  size?: "sm" | "md" | "lg";
}

export const PageHeader = forwardRef(function PageHeader(
  {
    icon,
    title,
    description,
    actions,
    className,
    sticky = false,
    onBack,
    backText = "Back",
    size = "lg",
  }: PropsWithChildren<PageHeaderProps>,
  ref: React.Ref<HTMLDivElement>
) {
  return (
    <>
      {sticky && (
        <Flex
          px="6"
          py="2"
          direction="column"
          gap="2"
          className={cx(styles.sticky, "shadow-sm")}
        >
          <Flex justify="between" align="center" gap="4">
            {icon && <Flex className={styles.iconContainer}>{icon}</Flex>}

            {actions && (
              <Flex flexShrink={"0"} gap="2" align="center">
                {actions}
              </Flex>
            )}
          </Flex>

          <Flex direction="column" gap="2">
            <Text size={sizeMap[size].sticky} weight="medium" highContrast className={styles.title}>
              {title}
            </Text>

            {description && (
              <Text size="2" color="gray">
                {description}
              </Text>
            )}
          </Flex>
        </Flex>
      )}

      <Flex py={onBack ? "2" : sizeMap[size].py} direction="column" gap="2">
        {onBack && (
          <Flex pl="2">
            <Button variant="ghost" size="2" onClick={onBack}>
              <ChevronLeftIcon />
              {backText}
            </Button>
          </Flex>
        )}

        <Flex
          ref={ref}
          width="100%"
          px="6"
          direction="column"
          gap="2"
          className={className}
        >
          <Flex
            width="100%"
            direction="row"
            gap="2"
            justify="between"
            align="start"
          >
            {icon && <Flex className={styles.iconContainer}>{icon}</Flex>}

            {actions && (
              <Flex flexShrink={"0"} gap="2" align="center">
                {actions}
              </Flex>
            )}
          </Flex>

          <Flex direction="column" gap="2">
            <Text size={sizeMap[size].content} weight="medium" highContrast className={styles.title}>
              {title}
            </Text>

            {description && (
              <Text size="3" color="gray">
                {description}
              </Text>
            )}
          </Flex>
        </Flex>
      </Flex>
    </>
  );
});
